import { c as _c } from "react/compiler-runtime";
import { type TrialOnboardingProgressControllerFindForTrialQueryResponse, trialOnboardingProgressControllerCreateMutationKey, trialOnboardingProgressControllerFindForTrialQueryKey, trialOnboardingProgressControllerUpdateMutationKey } from "@dc/gateway-jsonapi";
import { useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { type TrialOnboardingState, trialOnboardingStateSchema } from "./trialOnboardingProgressState";
export type TrialOnboardingProgress = {
  id: string;
  state: TrialOnboardingState;
  completed: boolean;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
};
const trialOnboardingProgressResourceSchema = z.object({
  id: z.string(),
  attributes: z.object({
    state: trialOnboardingStateSchema,
    completed: z.boolean(),
    completedAt: z.iso.datetime().nullable(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime()
  })
});
export function trialOnboardingProgressParams(trialId: string | undefined) {
  return {
    filter: {
      trialId: trialId ?? ""
    }
  };
}
export function trialOnboardingProgressStartMutationKey(trialId: string | undefined) {
  return [...trialOnboardingProgressControllerCreateMutationKey(), {
    trialId
  }] as const;
}
export function trialOnboardingProgressCompleteMutationKey(progressId: string | undefined) {
  return [...trialOnboardingProgressControllerUpdateMutationKey(), {
    operation: "complete",
    progressId
  }] as const;
}
export function selectTrialOnboardingProgress(response: TrialOnboardingProgressControllerFindForTrialQueryResponse): TrialOnboardingProgress | null {
  const resource = response.data[0];
  if (!resource) return null;
  const {
    id,
    attributes
  } = trialOnboardingProgressResourceSchema.parse(resource);
  return {
    id,
    state: attributes.state,
    completed: attributes.completed,
    completedAt: attributes.completedAt,
    createdAt: attributes.createdAt,
    updatedAt: attributes.updatedAt
  };
}
export function useTrialOnboardingProgressCache(trialId) {
  const $ = _c(11);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== trialId) {
    const params = trialOnboardingProgressParams(trialId);
    t0 = trialOnboardingProgressControllerFindForTrialQueryKey(params);
    $[0] = trialId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const queryKey = t0;
  let t1;
  if ($[2] !== queryClient || $[3] !== queryKey) {
    t1 = function getProgress() {
      const response = queryClient.getQueryData(queryKey);
      return response ? selectTrialOnboardingProgress(response) : undefined;
    };
    $[2] = queryClient;
    $[3] = queryKey;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const getProgress = t1;
  let t2;
  if ($[5] !== queryClient || $[6] !== queryKey) {
    t2 = async function setProgress(resource) {
      const response_0 = {
        data: [resource]
      } as TrialOnboardingProgressControllerFindForTrialQueryResponse;
      const progress = selectTrialOnboardingProgress(response_0);
      await queryClient.cancelQueries({
        queryKey,
        exact: true
      });
      queryClient.setQueryData(queryKey, response_0);
      return progress;
    };
    $[5] = queryClient;
    $[6] = queryKey;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const setProgress = t2;
  let t3;
  if ($[8] !== getProgress || $[9] !== setProgress) {
    t3 = {
      getProgress,
      setProgress
    };
    $[8] = getProgress;
    $[9] = setProgress;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  return t3;
}
const inFlightProgressMutations = new Map<string, Promise<unknown>>();

// Mutation keys share pending state, but React Query does not deduplicate mutation execution.
export function deduplicateProgressMutation<T>(key: string, create: () => Promise<T>): Promise<T> {
  const existing = inFlightProgressMutations.get(key) as Promise<T> | undefined;
  if (existing) return existing;
  const request = create();
  inFlightProgressMutations.set(key, request);
  const clearRequest = () => {
    if (inFlightProgressMutations.get(key) === request) inFlightProgressMutations.delete(key);
  };
  void request.then(clearRequest, clearRequest);
  return request;
}